import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Buildings, Books, FilePdf, PencilLine, GraduationCap, ArrowRight, Database, CircleNotch, FolderSimple } from "@phosphor-icons/react";

const BOARD_LABEL = { kpsc: "KPSC", ksp: "KSP", kea: "KEA" };
const BOARD_ACCENT = { kpsc: "#1E3A8A", ksp: "#0F766E", kea: "#B45309" };

const Count = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
        <Icon size={13} weight="duotone" />
        <span className="font-bold text-slate-800 tabular-nums">{value}</span>
        <span>{label}</span>
    </div>
);

export default function AdminRepositories() {
    const [repos, setRepos] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/admin/repositories"); setRepos(data); }
            catch (e) { setError(e.response?.data?.detail || "Failed to load repositories"); }
        })();
    }, []);

    if (error) return <div className="text-red-700 text-sm">{error}</div>;
    if (!repos) return <div className="text-slate-500 flex items-center gap-2 text-sm"><CircleNotch size={16} className="animate-spin" /> Loading repositories…</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-start gap-3 border-2 border-dashed border-[#BFD3FF] bg-blue-50/40 rounded-2xl p-5">
                <Database size={28} weight="duotone" className="text-[#1E3A8A] shrink-0 mt-0.5" />
                <div>
                    <h2 className="font-heading font-bold text-lg">Exam Repositories</h2>
                    <p className="text-sm text-slate-600 mt-1">
                        Each exam has its own scoped repository — Questions, PYQ Papers, Mains Prompts and Study Material can be attached to it and reach only the students preparing for that exam.
                    </p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {repos.map((r) => {
                    const accent = BOARD_ACCENT[r.board] || "#1E3A8A";
                    const total = (r.counts?.questions || 0) + (r.counts?.pyq_papers || 0) + (r.counts?.mains_prompts || 0) + (r.counts?.study_materials || 0) + (r.counts?.documents || 0);
                    return (
                        <Link
                            key={r.slug}
                            to={`/admin/repositories/${r.slug}`}
                            data-testid={`repo-card-${r.slug}`}
                            className="group bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 hover:border-[#1E3A8A] hover:-translate-y-0.5 transition-all"
                        >
                            <div className="flex items-start justify-between gap-2 mb-3">
                                <div>
                                    <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-md border" style={{ borderColor: `${accent}33`, backgroundColor: `${accent}10`, color: accent }}>
                                        {BOARD_LABEL[r.board] || r.board}
                                    </span>
                                    <h3 className="font-heading font-black text-2xl mt-2 leading-tight">{r.name}</h3>
                                    <div className="text-[11px] text-slate-500 mt-0.5">{r.full_name}</div>
                                </div>
                                <Buildings size={32} weight="duotone" style={{ color: accent }} />
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed">{r.description}</p>
                            <div className="mt-4 pt-3 border-t border-[#BFD3FF]/60 flex flex-wrap gap-x-4 gap-y-1.5">
                                <Count icon={Books} label="Questions" value={r.counts?.questions || 0} />
                                <Count icon={FolderSimple} label="Docs" value={r.counts?.documents || 0} />
                                <Count icon={FilePdf} label="PYQ" value={r.counts?.pyq_papers || 0} />
                                <Count icon={PencilLine} label="Mains" value={r.counts?.mains_prompts || 0} />
                                <Count icon={GraduationCap} label="Study" value={r.counts?.study_materials || 0} />
                            </div>
                            <div className="mt-4 flex items-center justify-between">
                                <span className="text-[11px] text-slate-500">{total === 0 ? "Empty — start populating" : `${total} total item(s)`}</span>
                                <span className="text-xs font-semibold text-[#1E3A8A] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                    Open <ArrowRight size={12} weight="bold" />
                                </span>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
